import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { validateSignup } from '../services/supabase';
import './LoginPage.css';

const SignupPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'validate' | 'signup' | 'confirm-email'>('validate');
  const [validationResult, setValidationResult] = useState<any>(null);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleValidateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setIsLoading(true);

    try {
      const result = await validateSignup(email);
      setValidationResult(result);

      if (!result.allowed) {
        setError(result.message);
      } else {
        setInfo(result.message);
        setStep('signup');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to validate email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      const result = await signUp(email, password, fullName);
      if (!result.success) {
        setError(result.error || 'Signup failed');
        return;
      }

      // Check if we have a session (email confirmation disabled) or not (confirmation required)
      if (result.needsEmailConfirmation) {
        // Show email confirmation message
        setStep('confirm-email');
      } else {
        // Session available, proceed to complete signup
        if (result.needsSetup) {
          navigate('/setup-project');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    if (step === 'confirm-email') {
      return (
        <div className="confirm-email-container">
          <div className="confirm-email-icon">📧</div>
          <h2>Check Your Email</h2>
          <p>We've sent a confirmation link to:</p>
          <p className="email-highlight">{email}</p>
          <p className="confirm-instructions">
            Click the link in the email to activate your account, then come back here to sign in.
          </p>
          <Link to="/login" className="login-button" style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}>
            Go to Sign In
          </Link>
        </div>
      );
    }

    if (step === 'validate') {
      return (
        <form onSubmit={handleValidateEmail} className="login-form">
          {error && <div className="error-message">{error}</div>}
          {info && <div className="info-message">{info}</div>}

          <div className="form-group">
            <label htmlFor="email">Work Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              disabled={isLoading}
            />
            <small className="form-hint">Use your work email address</small>
          </div>

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Validating...' : 'Continue'}
          </button>
        </form>
      );
    }

    return (
      <form onSubmit={handleSignup} className="login-form">
        {error && <div className="error-message">{error}</div>}
        {info && <div className="info-message">{info}</div>}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input type="email" id="email" value={email} disabled className="input-disabled" />
        </div>

        <div className="form-group">
          <label htmlFor="fullName">Full Name</label>
          <input
            type="text" id="fullName" value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="John Doe" required disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password" id="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Min 6 characters" required disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            type="password" id="confirmPassword" value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password" required disabled={isLoading}
          />
        </div>

        <button type="submit" className="login-button" disabled={isLoading}>
          {isLoading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>
    );
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <svg width="40" height="40" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="32" height="32" rx="8" fill="url(#logo-gradient-signup)"/>
              <path d="M10 16L14 20L22 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <defs>
                <linearGradient id="logo-gradient-signup" x1="0" y1="0" x2="32" y2="32">
                  <stop stopColor="#6366f1"/>
                  <stop offset="1" stopColor="#8b5cf6"/>
                </linearGradient>
              </defs>
            </svg>
            <h1>ComplyDeck</h1>
          </div>
          <p>{step === 'confirm-email' ? 'Almost there!' : 'Create your account'}</p>
        </div>

        {renderStep()}

        {step !== 'confirm-email' && (
          <div className="login-footer">
            <p className="auth-link">Already have an account? <Link to="/login">Sign in</Link></p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupPage;

